import {inject, Injectable} from '@angular/core';
import {FieldValueService} from './field-value.service';
import {FieldParameters} from './definitions/field-parameters';
import {SearchFilters} from './definitions/search-filters';
import {UserCacheService} from './user-cache.service';
import {ReferenceFilter} from './definitions/reference';
import {LoggerService} from './logger.service';
import {SearchReferenceService} from "./search-reference.service";
import {SearchService} from "./search.service";
import {SearchParameters} from "./definitions/search-parameters";

@Injectable({
  providedIn: 'root'
})
export class ReferenceFilterService {
  // Injections
  private readonly fieldValueSvc: FieldValueService = inject(FieldValueService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly searchReferenceService: SearchReferenceService = inject(SearchReferenceService);
  private readonly searchService: SearchService = inject(SearchService);
  private readonly userCacheService: UserCacheService = inject(UserCacheService);

  /* Used for setting search filter or create a new item */
  async generateRefFilterData(refFilters: ReferenceFilter[], isTargetArray: boolean, fieldParameters: FieldParameters): Promise<SearchFilters> {
    const target = new SearchFilters();
    refFilters = refFilters || [];
    refFilters = Array.isArray(refFilters) ? refFilters : [refFilters];
    for (const rFilter of refFilters) {
      const name = rFilter.filter_field;
      const values = rFilter.values;
      if (name && Array.isArray(values)) {
        await this.loopFilterValuesSetTargetSearchFilters(fieldParameters, name, values, target, isTargetArray)
      }
      if (rFilter.fq) {
        const fq = await this.getCheckSubstituteValue(rFilter.fq, fieldParameters);
        target.fq = target.fq || [];
        target.fq.push(fq);
      }
    }
    return target;
  }

  private async loopFilterValuesSetTargetSearchFilters(
    fieldParameters: FieldParameters,
    name: string,
    values: any[],
    target: SearchFilters,
    isTargetArray: boolean) {
    for (const [index, value] of values.entries()) {
      let targetVal = value;
      if (typeof value === 'object') {
        targetVal = this.getSourceFieldVal(value, fieldParameters);
      } else if (typeof value === 'string') {
        targetVal = await this.getCheckSubstituteValue(value, fieldParameters);
      }
      if (targetVal || targetVal === 0 || targetVal === false) {
        if (isTargetArray) {
          this.setArrayTargetFilters(target, name, targetVal);
        } else if (index === 0) {
          target[name] = targetVal;
        }
      }
    }
  }

  private setArrayTargetFilters(target: SearchFilters, name: string, targetVal: any) {
    if (Array.isArray(targetVal)) {
      if (targetVal.length) {
        target[name] = target[name] || [];
        // Remove duplicates from the targetVal
        targetVal = targetVal.filter((val, index, self) => self.indexOf(val) === index);
        target[name] = target[name].concat(targetVal);
      }
    } else {
      target[name] = target[name] || [];
      target[name].push(targetVal);
    }
  }

  private async getCheckSubstituteValue(value: string, fieldParameters: FieldParameters): Promise<string> {
    let res = value;
    if (value.includes('{')) {
      const substStr = this.fieldValueSvc.getSubstituteString(value, '{', '}');
      let substVal: any;
      if (substStr.startsWith('user.')) {
        const userField = substStr.split('.')[1];
        const userData = await this.userCacheService.getUserData();
        substVal = userData[userField];
      } else {
        substVal = await this.getSuperobjectTypeFilterFromDialogParent(substStr, fieldParameters);
        if (!substVal) {
          substVal = this.fieldValueSvc.getFieldValFromFieldParameters(fieldParameters, substStr);
        }
      }
      res = await this.getSubstituteValueResult(value, substStr, substVal);
      if (!substVal) {
        this.logger.warn(`Unable to find substitute value for ${substStr}, this may happen for field meta operations`);
      }
    }
    return res;
  }

  private async getSubstituteValueResult(value: string, substStr: string, substVal: string) {
    let res = '';
    if (substVal) {
      if (substStr === 'superobject_type_id') {
        const parentSuperobjectTypeId = await this.getParentSuperobjectTypeId(substVal);
        // Need both object superobject type id and parent superobject type id, e.g., superobject type id for "thing"
        // and "Object" superobject type id, which is thing's parent
        if (parentSuperobjectTypeId) {
          substVal = `("${substVal}" OR "${parentSuperobjectTypeId}")`;
        }
      }
      res = value.replace('{' + substStr + '}', substVal || '');
    }
    return res;
  }

  // If the concept field is placed in an edit dialog, e.g., when creating a new Designation while editing an object,
  // then the 'main type' selector must get the superobject type filter from the parent object. This filter is located
  // in the 'filters'-field in the rootObject (the root object is the new designation object)
  private async getSuperobjectTypeFilterFromDialogParent(substStr: string, fieldParameters: FieldParameters) {
    let substVal: string | string[];
    const reference = this.searchReferenceService.getSearchReferenceFromField(fieldParameters.field);
    if (substStr === 'superobject_type_id') {
      if (fieldParameters.rootObject.filters && fieldParameters.rootObject.object_type === reference.object_type) {
        const superobjectTypeFilter = fieldParameters.rootObject.filters.find(filter => filter.superobject_type_id !== undefined);
        if (superobjectTypeFilter) {
          substVal = superobjectTypeFilter.superobject_type_id;
        }
      } else if (fieldParameters.sectionsContainer.parentObject) {
        // This will happen when e.g., searching for history event types in a dialog, need to get the superobject
        // type filter from the parent
        substVal = fieldParameters.sectionsContainer.parentObject.superobject_type_id;
      }
    }
    return substVal;
  }

  private async getParentSuperobjectTypeId(superobjectTypeId: string) {
    let res = '';
    const searchParameters = {
      fq: [`artifact_id:${superobjectTypeId}`],
      fl: ['parent_id']
    } as SearchParameters;
    const searchRes = await this.searchService.search(searchParameters);
    if (searchRes.artifacts.length) {
      res = searchRes.artifacts[0].parent_id;
    }
    return res;
  }

  private getSourceFieldVal(sourceInfo: any, fieldParameters: FieldParameters) {
    let sourceVal = null;
    const sourceField = sourceInfo.source_field;
    if (sourceField) {
      if (sourceInfo['source_object']) {
        if (sourceInfo['source_object'] === 'parent_object') {
          sourceVal = this.getSourceValFromParentObject(fieldParameters, sourceField);
        } else if (sourceInfo['source_object'] === 'operationContextObject') {
          sourceVal = this.getSourceValFromOperationContextObject(fieldParameters, sourceField);
        } else {
          this.logger.warn('Source object not supported: ' + sourceInfo['source_object']);
        }
      } else {
        sourceVal = this.fieldValueSvc.getFieldValFromFieldParameters(fieldParameters, sourceField);
      }
    }
    return sourceVal;
  }

  private getSourceValFromParentObject(fieldParameters: FieldParameters, sourceField: string) {
    let sourceVal = null;
    const parentObject = fieldParameters.sectionsContainer.parentObject;
    if (parentObject) {
      sourceVal = this.getSourceValFromObject(parentObject, sourceField);
    } else {
      this.logger.warn('Parent object not set');
    }
    return sourceVal;
  }

  private getSourceValFromOperationContextObject(fieldParameters: FieldParameters, sourceField: string) {
    let sourceVal = null;
    const operationContextObjects = fieldParameters.sectionsContainer.operationContextObjects;
    if (operationContextObjects?.length) {
      sourceVal = this.getSourceValFromObject(operationContextObjects[0], sourceField);
    } else {
      this.logger.warn('Operation context object not set');
    }
    return sourceVal;
  }

  private getSourceValFromObject(sourceObject: any, sourceField: string) {
    const fieldParts = sourceField.split('.');
    let currentObj = sourceObject;
    for (const part of fieldParts) {
      if (currentObj) {
        if (Array.isArray(currentObj)) {
          currentObj = currentObj.map(item => item[part]);
        } else {
          currentObj = currentObj[part];
        }
      } else {
        this.logger.warn(`Unable to find nested field ${sourceField} in object`);
        break;
      }
    }
    return currentObj;
  }

}
